import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { View } from "ui/atoms/view";
import { Button } from "ui/gluestack/button";
import { Text } from "react-native";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useCancelAppointmentMutation } from "app/services/api/calendarApi";
import { format } from "date-fns";

export interface CancelEventModalRef {
  show: (calendarEventId: number | string, startDate?: Date | null) => void;
}

interface Props {
  onSuccess?: () => void;
}

const CancelEventModal = forwardRef<CancelEventModalRef, Props>(
  ({ onSuccess }, ref) => {
    const [showing, setShowing] = useState(false);
    const [calendarEventId, setCalendarEventId] = useState<number | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const { showErrorToast, showNewToast } = useDisplayToast();
    const [cancelAppointment, { isLoading }] = useCancelAppointmentMutation();

    useImperativeHandle(ref, () => ({
      show: (calendarEventId: number | string, date: Date) => {
        setCalendarEventId(Number(calendarEventId));
        setStartDate(date);
        setShowing(true);
      },
    }));

    const handleClose = () => {
      setShowing(false);
    };

    const handleCancel = async () => {
      try {
        if (!startDate)
          throw new Error("We need a start date to be able to cancel");

        await cancelAppointment({
          id: calendarEventId!,
          body: {
            occurrence_date: format(startDate, "yyyy-MM-dd"),
          },
        }).unwrap();
        showNewToast({
          title: "Appointment cancelled",
          message: "The appointment has been cancelled successfully.",
          type: "success",
        });
        handleClose();
        onSuccess?.();
      } catch (e) {
        showErrorToast(e);
      }
    };

    return (
      <ReusableModal
        isVisible={showing}
        onClose={handleClose}
        className="w-full max-w-[378px] mx-auto"
        title="Are you sure you want to cancel this appointment?"
        description="This action will remove this appointment permanently and cannot be undone. Please confirm if you want to proceed."
        hasCancelButton
      >
        <View className="flex flex-row justify-end gap-x-4 ">
          <Button
            action="primary"
            size="sm"
            variant="outline"
            onPress={handleClose}
          >
            <Text className="text-primary-500 font-semibold">Back</Text>
          </Button>
          <Button
            action="negative"
            size="sm"
            disabled={isLoading}
            onPress={handleCancel}
          >
            <Text className="text-white">Cancel</Text>
          </Button>
        </View>
      </ReusableModal>
    );
  },
);

CancelEventModal.displayName = "CancelEventModal";
export { CancelEventModal };
